import * as React from 'react';

import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Loader2, XIcon } from 'lucide-react';

import { cn } from '@/lib/utils';
import { useDialogStore } from '@/store';
import { VARIANT_CONFIG } from '@/types/dialog';

import { Button } from './button';

function Dialog({
	...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) {
	return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

function DialogTrigger({
	...props
}: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
	return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({
	...props
}: React.ComponentProps<typeof DialogPrimitive.Portal>) {
	return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose({
	...props
}: React.ComponentProps<typeof DialogPrimitive.Close>) {
	return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

function DialogOverlay({
	className,
	...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
	return (
		<DialogPrimitive.Overlay
			data-slot="dialog-overlay"
			className={cn(
				'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50',
				className
			)}
			{...props}
		/>
	);
}

function DialogContent({
	className,
	children,
	showCloseButton = true,
	...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
	showCloseButton?: boolean
}) {
	return (
		<DialogPortal data-slot="dialog-portal">
			<DialogOverlay />
			<DialogPrimitive.Content
				data-slot="dialog-content"
				className={cn(
					'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed top-[50%] left-[50%] z-50 grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg border bg-white p-6 shadow-lg duration-200 sm:max-w-lg',
					className
				)}
				{...props}
			>
				{children}
				{showCloseButton && (
					<DialogPrimitive.Close
						data-slot="dialog-close"
						className="ring-offset-background focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute top-4 right-4 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"
					>
						<XIcon />
						<span className="sr-only">Close</span>
					</DialogPrimitive.Close>
				)}
			</DialogPrimitive.Content>
		</DialogPortal>
	);
}

function DialogHeader({ className, ...props }: React.ComponentProps<'div'>) {
	return (
		<div
			data-slot="dialog-header"
			className={cn('flex flex-col gap-2 text-center sm:text-left', className)}
			{...props}
		/>
	);
}

function DialogFooter({ className, ...props }: React.ComponentProps<'div'>) {
	return (
		<div
			data-slot="dialog-footer"
			className={cn(
				'flex flex-col-reverse gap-2 sm:flex-row sm:justify-end',
				className
			)}
			{...props}
		/>
	);
}

function DialogTitle({
	className,
	...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
	return (
		<DialogPrimitive.Title
			data-slot="dialog-title"
			className={cn('leading-none', className)}
			{...props}
		/>
	);
}

function DialogDescription({
	className,
	...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
	return (
		<DialogPrimitive.Description
			data-slot="dialog-description"
			className={cn('text-muted-foreground text-sm', className)}
			{...props}
		/>
	);
}

function StatusDialog() {
	const open = useDialogStore((s) => s.open);

	const title = useDialogStore((s) => s.title);
	const description = useDialogStore((s) => s.description);
	const cancelText = useDialogStore((s) => s.cancelText);
	const confirmText = useDialogStore((s) => s.confirmText);
	const variant = useDialogStore((s) => s.variant);
	const isLoading = useDialogStore((s) => s.isLoading);

	const onConfirm = useDialogStore((s) => s.onConfirm);
	const onCancel = useDialogStore((s) => s.onCancel);
	const onOpenChange = useDialogStore((s) => s.onOpenChange);

	const { icon: Icon, colorClass, defaultConfirmText } = VARIANT_CONFIG[variant];

	const showCancel = variant === 'warning' || !!cancelText;

	const handleConfirm = () => {
		if (onConfirm) {
			onConfirm();
		} else {
			onOpenChange(false);
		}
	};

	const handleCancel = () => {
		if (onCancel) onCancel();
		onOpenChange(false);
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent
				className="overflow-visible pt-12 text-center sm:max-w-[400px] [&>button]:hidden"
				id="check"
			>
				<div className="absolute -top-12 left-1/2 -translate-x-1/2 rounded-full bg-white p-2 shadow-sm">
					<div
						className={cn(
							'flex h-20 w-20 items-center justify-center rounded-full text-white shadow-inner',
							colorClass
						)}
					>
						<Icon size={40} strokeWidth={3} />
					</div>
				</div>

				<DialogHeader className="mb-4">
					<DialogTitle className="text-foreground text-center text-xl font-bold">
						{title}
					</DialogTitle>
					{description && (
						<DialogDescription className="text-muted-foreground mt-2 text-center">
							{description}
						</DialogDescription>
					)}
				</DialogHeader>

				<DialogFooter className="flex w-full gap-2 sm:justify-center sm:space-x-0">
					{showCancel && (
						<Button
							variant="outline"
							className="border-primary text-primary hover:bg-primary/5 hover:text-primary w-auto px-10"
							onClick={handleCancel}
							disabled={isLoading}
						>
							{cancelText || 'Batal'}
						</Button>
					)}

					<Button
						className="bg-primary text-primary-foreground hover:bg-primary/90 w-auto px-10"
						onClick={handleConfirm}
						disabled={isLoading}
					>
						{isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
						{confirmText || defaultConfirmText}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

export {
	Dialog,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogOverlay,
	DialogPortal,
	DialogTitle,
	DialogTrigger,
	StatusDialog
};
